<template>
  <PortalForm
    :portalType="portalType"
    title="Policy Cancellation Request"
    :headerIconSrc="cancellationRequestIcon"
    :steps="steps"
    :current-step="currentStep"
    :form-data="formData"
    :form-valid="isStepValid"
    :loading="loading"
    :success-title="successTitle"
    :success-message="successMessage"
    :reference-number="referenceNumber"
    :success-details="successDetails"
    :next-steps="nextSteps"
    :enable-email-receipt="true"
    :user-email="formData.email"
    :form-id="'cancellation-request'"
    @update:formData="updateFormData"
    @step-change="handleStepChange"
    @form-submit="submitForm"
    @draft-restored="handleDraftRestored"
    @back="navigateAway"
    @primary-action="returnToDashboard"
    @email-sent="handleEmailSent"
  >
    <!-- Contact Information Step -->
    <SharedContactInformationStep 
      v-if="currentStep === 1"
      v-model:firstName="formData.firstName"
      v-model:lastName="formData.lastName"
      v-model:policyNumber="formData.policyNumber"
      v-model:phoneNumber="formData.phoneNumber"
      v-model:email="formData.email"
      :customerType="portalType"
      :errors="errors"
      @next="goToNextStep"
    />
    
    <!-- Cancellation Reason Step -->
    <SharedCancellationReasonStep
      v-if="currentStep === 2"
      v-model:reason="formData.reason"
      v-model:realtorInfo="formData.realtorInfo"
      v-model:callbackTime="formData.callbackTime"
      v-model:isWinBackSelected="formData.isWinBackSelected"
      v-model:dynamicFields="formData.dynamicFields"
      :customerType="portalType"
      :errors="errors"
      @previous="goToPreviousStep"
      @next="goToNextStep"
    />
    
    <!-- Document Upload Step -->
    <SharedDocumentUploadStep
      v-if="currentStep === 3"
      v-model:documents="formData.documents"
      :customerType="portalType"
      :errors="errors"
      :document-types="dynamicDocumentTypes"
      @previous="goToPreviousStep"
      @next="goToNextStep"
    />
    
    <!-- Terms and Review Step -->
    <TermsAndSubmitStep
      v-if="currentStep === 4"
      v-model:comments="formData.comments"
      v-model:userAgrees="formData.userAgrees"
      :reason="formData.reason"
      :realtorInfo="formData.realtorInfo"
      :isWinBackSelected="formData.isWinBackSelected"
      :callbackTime="formData.callbackTime"
      :dynamicFields="formData.dynamicFields"
      :customerType="portalType"
      :isSubmitting="isSubmitting"
      :errors="errors"
      @previous="goToPreviousStep"
      @submit="handleSubmit"
    />
  </PortalForm>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import PortalForm from '@/components/common/PortalForm.vue';
import SharedContactInformationStep from '@/components/customerservice/policy-management/forms/shared/steps/SharedContactInformationStep.vue';
import SharedCancellationReasonStep from '@/components/customerservice/policy-management/forms/shared/steps/SharedCancellationReasonStep.vue';
import SharedDocumentUploadStep from '@/components/customerservice/policy-management/forms/shared/steps/SharedDocumentUploadStep.vue';
import TermsAndSubmitStep from './steps/TermsAndSubmitStep.vue';
import { validateEmail, validatePhoneNumber as validatePhone, validatePolicyNumber } from '@/utils/validators';
import { useBaseInsuranceService } from '@/services/baseInsuranceService';
import { useRouter } from 'vue-router';
import confetti from 'canvas-confetti';
import cancellationRequestIcon from '@/assets/images/ui/icons/cancellationRequest.svg';

// Props
const props = defineProps({
  portalType: {
    type: String,
    default: 'customer',
    validator: value => ['customer', 'lender'].includes(value)
  }
});

// Router for navigation
const router = useRouter();
const { submitForm: submitFormRequest } = useBaseInsuranceService();

// Form state
const currentStep = ref(1);
const loading = ref(false);
const isSubmitting = ref(false);
const errors = ref({});
const referenceNumber = ref('');

// Success state
const successTitle = ref('Cancellation Request Submitted');
const successMessage = ref('Your policy cancellation request has been received. Our team will review your request and process it accordingly.');
const successDetails = ref([]);
const nextSteps = ref([
  'A representative may contact you to discuss your request.',
  'If we can help you with other insurance needs, please contact us.',
  'You will receive a confirmation email with the details of your request.'
]);

// Form data - using a single reactive object for easier state management
const formData = ref({
  firstName: '',
  lastName: '',
  policyNumber: '',
  phoneNumber: '',
  email: '',
  reason: '',
  realtorInfo: {
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: ''
  },
  isWinBackSelected: false,
  callbackTime: {
    date: '',
    time: ''
  },
  dynamicFields: {},
  comments: '',
  userAgrees: false,
  documents: []
});

// Document types required for cancellation
const baseDocumentTypes = [
  { id: 'id_verification', label: 'ID Verification', description: 'A copy of your driver\'s license or ID', required: false },
  { id: 'additional_documentation', label: 'Additional Documentation', description: 'Any other documents supporting your cancellation request', required: false }
];

// Computed property for dynamic document types based on reason
const dynamicDocumentTypes = computed(() => {
  const reasonSpecificTypes = [];
  
  switch(formData.value.reason) {
    case 'selling_home':
      reasonSpecificTypes.push({ 
        id: 'property_sale_documents', 
        label: 'Property Sale Documents', 
        description: 'Closing documents if your property was sold', 
        required: true 
      });
      break;
      
    case 'new_insurer':
      reasonSpecificTypes.push({ 
        id: 'new_insurance_confirmation', 
        label: 'New Insurance Confirmation', 
        description: 'Proof of new insurance coverage', 
        required: true 
      });
      break;
      
    case 'ownership_change':
      reasonSpecificTypes.push({ 
        id: 'ownership_transfer_documents', 
        label: 'Ownership Transfer Documentation', 
        description: 'Legal documents showing transfer of ownership', 
        required: true 
      });
      break;
      
    case 'risk_reduction':
      reasonSpecificTypes.push({ 
        id: 'risk_reduction_proof', 
        label: 'Risk Reduction Documentation', 
        description: 'Documentation of changes that reduced risk', 
        required: true 
      });
      break;
      
    case 'mortgage_change':
      reasonSpecificTypes.push({ 
        id: 'mortgage_payoff_documents', 
        label: 'Mortgage Payoff Documents', 
        description: 'Proof that mortgage has been paid off', 
        required: true 
      });
      break;
      
    case 'moving':
      reasonSpecificTypes.push({ 
        id: 'new_address_proof', 
        label: 'New Address Documentation', 
        description: 'Proof of your new address (utility bill, lease, etc.)', 
        required: true 
      });
      
      if (formData.value.dynamicFields?.moving?.isPropertySold) {
        reasonSpecificTypes.push({ 
          id: 'property_sale_documents', 
          label: 'Property Sale Documents', 
          description: 'Closing documents if your property was sold', 
          required: true 
        });
      }
      break;
      
    case 'financial':
      reasonSpecificTypes.push({ 
        id: 'financial_hardship_documentation', 
        label: 'Financial Hardship Documentation', 
        description: 'Documentation supporting your financial hardship claim', 
        required: true 
      });
      break;
      
    case 'consolidation':
      reasonSpecificTypes.push({ 
        id: 'new_policy_confirmation', 
        label: 'New Consolidated Policy Confirmation', 
        description: 'Proof of your new consolidated insurance policy', 
        required: true 
      });
      break;
      
    default:
      break;
  }
  
  return [...baseDocumentTypes, ...reasonSpecificTypes];
});

// Computed properties
const steps = computed(() => [
  { label: 'Contact Information', index: 1 },
  { label: 'Cancellation Reason', index: 2 },
  { label: 'Documents', index: 3 },
  { label: 'Review & Terms', index: 4 }
]);

// Check if current step is valid
const isStepValid = computed(() => {
  if (currentStep.value === 1) {
    return validateStep1(false);
  } else if (currentStep.value === 2) {
    return validateStep2(false);
  } else if (currentStep.value === 3) {
    return validateStep3(false);
  } else if (currentStep.value === 4) {
    return validateStep4(false);
  }
  return true;
});

// Validation methods - returns validation result but only sets errors if updateErrors is true
const validateStep1 = (updateErrors = true) => {
  const tempErrors = {
    firstName: !formData.value.firstName ? 'First name is required' : '',
    lastName: !formData.value.lastName ? 'Last name is required' : '',
    policyNumber: validatePolicyNumber(formData.value.policyNumber),
    phoneNumber: validatePhone(formData.value.phoneNumber),
    email: validateEmail(formData.value.email)
  };
  
  const isValid = !Object.values(tempErrors).some(error => error);
  
  if (updateErrors) {
    errors.value = { ...errors.value, ...tempErrors };
  }
  
  return isValid;
};

const validateStep2 = (updateErrors = true) => {
  const tempErrors = {
    reason: !formData.value.reason ? 'Please select a reason for cancellation' : ''
  };
  
  // If selling home, validate realtor info - all fields now optional
  if (formData.value.reason === 'selling_home') {
    // No validation needed, all fields are optional
  }
  
  // If win-back selected, validate callback time
  if (formData.value.isWinBackSelected) {
    tempErrors.callbackDate = !formData.value.callbackTime.date ? 'Please select a callback date' : '';
    tempErrors.callbackTime = !formData.value.callbackTime.time ? 'Please select a callback time' : '';
  }
  
  // Validate dynamic fields based on reason
  if (formData.value.reason === 'new_insurer') {
    tempErrors.newInsurerName = !formData.value.dynamicFields?.newInsurer?.name ? 'New insurer name is required' : '';
    // Policy number now optional
  } else if (formData.value.reason === 'ownership_change') {
    // New owner name now optional
    tempErrors.ownershipChangeReason = !formData.value.dynamicFields?.ownershipChange?.reason ? 'Reason for ownership change is required' : '';
  } else if (formData.value.reason === 'risk_reduction') {
    tempErrors.riskReductionDescription = !formData.value.dynamicFields?.riskReduction?.description ? 'Description of changes is required' : '';
  } else if (formData.value.reason === 'mortgage_change') {
    tempErrors.previousMortgageCompany = !formData.value.dynamicFields?.mortgageChange?.previousCompany ? 'Previous mortgage company is required' : '';
    // Loan number now optional
  } else if (formData.value.reason === 'moving') {
    // New address now optional
    tempErrors.isPropertySold = formData.value.dynamicFields?.moving?.isPropertySold === undefined ? 'Please indicate if the property is sold' : '';
  } else if (formData.value.reason === 'financial') {
    tempErrors.hardshipType = !formData.value.dynamicFields?.financial?.hardshipType ? 'Hardship type is required' : '';
    // Duration and coverage needs now optional
  } else if (formData.value.reason === 'consolidation') {
    // Policy number now optional
    tempErrors.insuranceProvider = !formData.value.dynamicFields?.consolidation?.provider ? 'Insurance provider is required' : '';
  }
  
  const isValid = !Object.values(tempErrors).some(error => error);
  
  if (updateErrors) {
    errors.value = { ...errors.value, ...tempErrors };
  }
  
  return isValid;
};

const validateStep3 = (updateErrors = true) => {
  // Document validation logic
  // Making all document uploads optional for customers cancelling policies
  return true;
  
  /* Original validation logic commented out
  // First check if documentTypes is properly defined
  if (!dynamicDocumentTypes.value || !Array.isArray(dynamicDocumentTypes.value)) {
    return true; // If no document types are defined, assume validation passes
  }
  
  const requiredDocTypes = dynamicDocumentTypes.value.filter(type => type.required);
  if (!requiredDocTypes.length) {
    return true; // No required documents
  }
  
  const requiredDocIds = requiredDocTypes.map(type => type.id);
  
  // Check if documents array exists and is an array
  if (!formData.value.documents || !Array.isArray(formData.value.documents)) {
    const tempErrors = {
      documents: 'Please upload all required documents'
    };
    
    if (updateErrors) {
      errors.value = { ...errors.value, ...tempErrors };
    }
    
    return false;
  }
  
  const hasAllRequiredDocs = requiredDocIds.every(docId => 
    formData.value.documents.some(doc => doc && doc.type === docId)
  );
  
  if (!hasAllRequiredDocs) {
    const tempErrors = {
      documents: 'Please upload all required documents'
    };
    
    if (updateErrors) {
      errors.value = { ...errors.value, ...tempErrors };
    }
    
    return false;
  }
  
  return true;
  */
};

const validateStep4 = (updateErrors = true) => {
  const tempErrors = {
    userAgrees: !formData.value.userAgrees ? 'You must agree to the terms and conditions' : ''
  };
  
  const isValid = !Object.values(tempErrors).some(error => error);
  
  if (updateErrors) {
    errors.value = { ...errors.value, ...tempErrors };
  }
  
  return isValid;
};

// Navigation methods
const handleStepChange = (newStep) => {
  currentStep.value = newStep;
};

const goToNextStep = () => {
  let isValid = false;
  
  if (currentStep.value === 1) {
    isValid = validateStep1();
  } else if (currentStep.value === 2) {
    isValid = validateStep2();
  } else if (currentStep.value === 3) {
    isValid = validateStep3();
  }
  
  if (isValid) {
    currentStep.value++;
  }
};

const goToPreviousStep = () => {
  if (currentStep.value > 1) {
    currentStep.value--;
  }
};

// Form data handling
const updateFormData = (newFormData) => {
  formData.value = newFormData;
};

const handleDraftRestored = (draft) => {
  // Any additional processing needed when a draft is restored
};

// Form submission
const handleSubmit = async () => {
  if (!validateStep4()) return;
  await submitForm();
};

// Add this method to flatten the nested structure before submission
const prepareFormDataForSubmission = () => {
  const formDataObj = new FormData();
  
  // Basic fields - Fixed naming convention to match backend expectations
  formDataObj.append('first_name', formData.value.firstName || '');
  formDataObj.append('last_name', formData.value.lastName || '');
  formDataObj.append('policy_number', formData.value.policyNumber || '');
  formDataObj.append('phone', formData.value.phoneNumber || '');
  formDataObj.append('email', formData.value.email || '');
  formDataObj.append('customer_type', props.portalType);
  formDataObj.append('reason', formData.value.reason || '');
  formDataObj.append('comments', formData.value.comments || '');
  formDataObj.append('user_agrees', formData.value.userAgrees ? 'true' : 'false');
  
  // Handle nested realtor info if it exists
  if (formData.value.reason === 'selling_home' && formData.value.realtorInfo) {
    formDataObj.append('realtor_first_name', formData.value.realtorInfo.firstName || '');
    formDataObj.append('realtor_last_name', formData.value.realtorInfo.lastName || '');
    formDataObj.append('realtor_email', formData.value.realtorInfo.email || '');
    formDataObj.append('realtor_phone', formData.value.realtorInfo.phoneNumber || '');
  }
  
  // Handle callback time if it exists
  if (formData.value.isWinBackSelected && formData.value.callbackTime) {
    formDataObj.append('callback_date', formData.value.callbackTime.date || '');
    formDataObj.append('callback_time', formData.value.callbackTime.time || '');
  }
  
  // Handle any dynamic fields that were created based on reason
  if (formData.value.dynamicFields) {
    // For each category of dynamic fields, add them with snake_case naming
    if (formData.value.dynamicFields.newInsurer) {
      formDataObj.append('new_insurer_name', formData.value.dynamicFields.newInsurer.name || '');
      formDataObj.append('new_policy_number', formData.value.dynamicFields.newInsurer.policyNumber || '');
      formDataObj.append('new_policy_date', formData.value.dynamicFields.newInsurer.effectiveDate || '');
    }
    
    if (formData.value.dynamicFields.ownershipChange) {
      formDataObj.append('new_owner_name', formData.value.dynamicFields.ownershipChange.newOwnerName || '');
      formDataObj.append('transfer_date', formData.value.dynamicFields.ownershipChange.transferDate || '');
      formDataObj.append('ownership_change_reason', formData.value.dynamicFields.ownershipChange.reason || '');
    }
    
    if (formData.value.dynamicFields.riskReduction) {
      formDataObj.append('risk_reduction_description', formData.value.dynamicFields.riskReduction.description || '');
      formDataObj.append('risk_reduction_date', formData.value.dynamicFields.riskReduction.date || '');
    }
    
    if (formData.value.dynamicFields.mortgageChange) {
      formDataObj.append('previous_mortgage_company', formData.value.dynamicFields.mortgageChange.previousCompany || '');
      formDataObj.append('mortgage_payoff_date', formData.value.dynamicFields.mortgageChange.payoffDate || '');
      formDataObj.append('loan_number', formData.value.dynamicFields.mortgageChange.loanNumber || '');
    }
    
    if (formData.value.dynamicFields.moving) {
      formDataObj.append('new_address', formData.value.dynamicFields.moving.newAddress || '');
      formDataObj.append('moving_date', formData.value.dynamicFields.moving.movingDate || '');
      formDataObj.append('is_property_sold', formData.value.dynamicFields.moving.isPropertySold ? 'true' : 'false');
    }
    
    if (formData.value.dynamicFields.financial) {
      formDataObj.append('hardship_type', formData.value.dynamicFields.financial.hardshipType || '');
      formDataObj.append('hardship_duration', formData.value.dynamicFields.financial.expectedDuration || '');
      formDataObj.append('alternative_coverage', formData.value.dynamicFields.financial.alternativeCoverage || '');
    }
    
    if (formData.value.dynamicFields.consolidation) {
      formDataObj.append('consolidated_policy_number', formData.value.dynamicFields.consolidation.policyNumber || '');
      formDataObj.append('insurance_provider', formData.value.dynamicFields.consolidation.provider || '');
      formDataObj.append('consolidation_effective_date', formData.value.dynamicFields.consolidation.effectiveDate || '');
    }
  }
  
  // Add documents if they exist
  if (formData.value.documents && formData.value.documents.length > 0) {
    formData.value.documents.forEach((doc, index) => {
      if (doc.file) {
        formDataObj.append(`document_${index}`, doc.file);
        formDataObj.append(`document_${index}_type`, doc.type || '');
      }
    });
  }
  
  return formDataObj;
};

const submitForm = async () => {
  isSubmitting.value = true;
  
  try {
    // Prepare flattened form data
    const flattenedFormData = prepareFormDataForSubmission();
    
    // Submit form data to the service
    await submitFormRequest('/cancellation-requests', flattenedFormData);
    
    // Set reference number for success message
    referenceNumber.value = `CANCEL-${Math.floor(Math.random() * 10000000)}`;
    
    // Set success details
    successDetails.value = [
      { label: 'Policy Number', value: formData.value.policyNumber },
      { label: 'Policyholder', value: `${formData.value.firstName} ${formData.value.lastName}` },
      { label: 'Reason', value: getCancellationReasonDisplay(formData.value.reason) }
    ];
    
    // Trigger confetti animation after successful submission
    setTimeout(() => {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { x: 0.5, y: 0.5 },
      });
    }, 500);
    
    // Set the current step to a value greater than the total steps
    // This will trigger the success screen in PortalForm
    currentStep.value = steps.value.length + 1;
    
    return true;
  } catch (error) {
    console.error('Error submitting form:', error);
    errors.value.submission = 'There was a problem submitting your request. Please try again.';
    return false;
  } finally {
    isSubmitting.value = false;
  }
};

// Helper function to get display text for cancellation reason
const getCancellationReasonDisplay = (reason) => {
  const reasonMap = {
    'non_renewal': 'Non-renewal',
    'replaced_policy': 'Replaced by another policy',
    'selling_home': 'Selling home',
    'too_expensive': 'Premium too expensive',
    'moving': 'Moving out of service area',
    'other': 'Other reasons'
  };
  
  return reasonMap[reason] || reason;
};

// Handle submission actions
const handleEmailSent = (email) => {
  console.log(`Confirmation email sent to ${email}`);
};

const returnToDashboard = () => {
  router.push('/dashboard');
};

const navigateAway = () => {
  router.push('/forms');
};
</script>

<style scoped>
.form-section {
  @apply p-4 md:p-6;
}
</style> 